import { Box, Skeleton, Table, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../../apis";
import { IBalanceSheetReport } from "../../../apis/ledger";
import { message } from "../../../utils/Message";
import { Amt, money } from "./report-format";
import { saveBlob } from "./report-download";
import { periodBounds, periodKey, useReportPeriod } from "./report-period";
import { ReportShell, StatusPill, UnmappedSection, withSectionHeaders } from "./ReportShell";
import classes from "./ReportShell.module.css";
import { ListStates, resolveListStates } from "../../../components/list";

const COLS = 2;

/** Баланс (SPRINT-05 §6.2) — Хөрөнгө = Өр төлбөр + Өмч. */
export const LedgerBalanceSheetPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const [period, setPeriod] = useReportPeriod();
  const [exporting, setExporting] = useState<null | "excel" | "pdf">(null);

  const { data, isLoading, error } = useSwr<IBalanceSheetReport>(
    `ledger.report.balanceSheet.${periodKey(period)}`,
    async () => await LedgerApi.getBalanceSheet(period),
    { revalidateOnFocus: false },
  );

  const lines = data?.lines ?? [];
  // `lines` report_line_map-аас ирдэг тул хоосон үед ч 0-ээр дүүрэн; `0 = 0 + 0`
  // нь isBalanced=true болгодог. `accountCount` л «тайлагнах зүйлгүй»-г ялгана.
  const isEmpty = !data || data.accountCount === 0 || lines.length === 0;
  const asOf = periodBounds(period).to;

  const runExport = async (format: "excel" | "pdf") => {
    setExporting(format);
    try {
      if (format === "excel") {
        saveBlob(await LedgerApi.exportReportExcel({ report: "balance-sheet", ...period }), `balance-sheet-${periodKey(period)}.xlsx`);
      } else {
        const { url } = await LedgerApi.exportReportPdf({ report: "balance-sheet", ...period });
        window.open(url, "_blank");
      }
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const liabilitiesAndEquity = data ? Number(data.liabilities) + Number(data.equity) : 0;

  const listStates = resolveListStates(
    <ListStates loading={isLoading} empty={isEmpty} columns={COLS} />
  );

  return (
    <ReportShell
      title={t("billing:ledger.report.balanceSheetTitle")}
      status={
        data && !isEmpty && (
          <StatusPill ok={data.isBalanced}>
            {data.isBalanced
              ? t("billing:ledger.report.equation")
              : t("billing:ledger.report.equationBroken", { amount: money(Math.abs(data.difference)) })}
          </StatusPill>
        )
      }
      subtitle={
        <>
          {t("billing:ledger.report.asOf", { date: asOf })}
          {" · "}
          <Link to={`/ledger/reports/income-statement?period=${periodKey(period)}`} className={classes.linkBtn}>
            {t("billing:ledger.report.toIncomeStatement")}
          </Link>
        </>
      }
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      onExport={{
        onExcel: () => runExport("excel"),
        onPdf: () => runExport("pdf"),
        excelLoading: exporting === "excel",
        pdfLoading: exporting === "pdf",
      }}
    >
      <Box className={classes.card}>
        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Tbody>
              {<>
                  {withSectionHeaders(lines).map((item, i) =>
                    item.kind === "section" ? (
                      <Table.Tr key={`section-${i}`} className={classes.groupRow}>
                        <Table.Td colSpan={COLS}>
                          {t(`billing:ledger.report.section.${item.section}`, { defaultValue: item.section })}
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      <Table.Tr key={item.row.lineCode} className={/_TOTAL$/.test(item.row.lineCode) ? classes.totalRow : classes.indent}>
                        <Table.Td>{item.row.lineName}</Table.Td>
                        <Table.Td className={classes.num}>
                          {/_TOTAL$/.test(item.row.lineCode) ? money(item.row.amount) : <Amt value={item.row.amount} />}
                        </Table.Td>
                      </Table.Tr>
                    ),
                  )}
                  <Table.Tr className={classes.grandRow}>
                    <Table.Td>{t("billing:ledger.report.liabilitiesAndEquity")}</Table.Td>
                    <Table.Td className={classes.num}>{money(liabilitiesAndEquity)}</Table.Td>
                  </Table.Tr>
                </>}
            </Table.Tbody>
          </Table>
          )}
        </Box>
      </Box>

      <UnmappedSection rows={data?.unmapped ?? []} />
    </ReportShell>
  );
};

export default LedgerBalanceSheetPage;
